import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { type LargeIcon, Wizard } from '@factorialco/deprecated-design-system'
import {
  AttendanceCustomTimeRangeDayEnum,
  SettingPageCustomTimeRangeFragment,
  SettingPageEmployeesSettingFragment,
} from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendDateTimeToUIUtcTime } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import ManageSettingVersionModal from 'modules/attendance/components/SettingItemPage/TimeTracking/ManageSettingVersionModal'
import { useAttendanceSettingWorkCategories } from 'modules/attendance/lib/attendanceSettingWorkCategoriesContext'
import { WizardData } from 'modules/attendance/lib/forms/utils/CustomTimeRangesFormData'
import { centsToMinutes } from 'modules/attendance/lib/timeConverter'
import { MODAL_LOCK_TIMEOUT } from 'modules/attendance/lib/utils'
import CustomTimeRangeUtils from 'modules/attendance/lib/utils/CustomTimeRangeUtils'

import { basicDetailsStep } from './BasicDetails'
import { compensationMethodStep } from './CompensationMethod'
import { hourlyRateStep } from './HourlyRate'
import { useUpsertCustomTimeRanges } from './useUpsertCustomTimeRanges'

const MINIMUM_MULTIPLIER = 1

interface Props {
  onClose: () => void
  range?: SettingPageCustomTimeRangeFragment
  dayType?: AttendanceCustomTimeRangeDayEnum
  isSpecificDateMode?: boolean
}

const SpecialHourWizard = ({ onClose, range, dayType, isSpecificDateMode }: Props) => {
  const [isLocked, setIsLocked] = React.useState(true)
  const {
    attendanceSetting,
    payrollEnabled,
    contractTaxonomies,
    payrollConcepts,
    payrollMappingsTaxonomies,
    payrollMappingsCompensations,
    canCreateSettingCategory,
    hasCompensations,
    hasSpecialHoursSpecificDates,
  } = useAttendanceSettingWorkCategories()

  const settingId = attendanceSetting.id
  const { onSubmit: upsertRanges, errors } = useUpsertCustomTimeRanges({
    settingId,
  })
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const day = dayType || range?.day

  const [openEffectiveAtFormModal, setOpenEffectiveAtFormModal] = React.useState(false)
  const isEdit = Boolean(range)

  // When opening the Wizard from the PopoverMenu, clicking on "Edit", the click event passes through
  // to the backdrop (overlay) and immediately closes the Wizard.
  // We temporarily lock the modal for ${MODAL_LOCK_TIMEOUT}ms to wait for the PopoverMenu
  // animation to complete, preventing click-outside interference.
  // Note: we can't use the built-in "modal" Wizard prop as its focus trap breaks form components.
  useEffect(() => {
    setTimeout(() => setIsLocked(false), MODAL_LOCK_TIMEOUT)
  }, [])

  const date = range?.date ?? undefined
  const days = day ? [day] : date || isSpecificDateMode ? undefined : []

  const initialData: WizardData = {
    id: range?.id.toString(),
    day: days,
    date: date,
    poolType: range?.poolType ?? 'virtual_timeoff',
    startTime: range?.startTime ? dateBackendDateTimeToUIUtcTime(range.startTime) : undefined,
    rawMinutes: range?.rawMinutesInCents ? centsToMinutes(range?.rawMinutesInCents) : 60,
    equivalentMinutes: range?.equivalentMinutesInCents
      ? centsToMinutes(range?.equivalentMinutesInCents)
      : 60,
    endTime: range?.endTime ? dateBackendDateTimeToUIUtcTime(range.endTime) : undefined,
    slotSelected: range && CustomTimeRangeUtils.isBase(range) ? 'full-day' : 'specific-time-slot',
    contractTaxonomies,
    payrollConcepts,
    payrollMappingsTaxonomies,
    payrollMappingsCompensations,
    errors,
    attendanceEmployeesSettingId: settingId.toString(),
    timeSettingsCustomTimeRangeCategoryId: range?.timeSettingsCustomTimeRangeCategoryId ?? 0,
    formMappingsTaxonomies: [],
    payrollEnabled,
    hasCompensations,
    hasSpecialHoursSpecificDates,
    canCreateSettingCategory,
    isEdit,
    hasInvalidMultiplier: function () {
      return this.equivalentMinutes / this.rawMinutes < MINIMUM_MULTIPLIER
    },
  }

  const [wizardData, setWizardData] = React.useState<WizardData>(initialData)
  const [newSettingData, setNewSettingData] = React.useState<WizardData | null>(null)

  const onCreateSetting = async (newSetting: SettingPageEmployeesSettingFragment) => {
    const newCustomTimeRanges = newSetting.customTimeRanges
    // Find the copy of the time block edited in the newly created setting
    const matchingRange = CustomTimeRangeUtils.findMatchingRange(newCustomTimeRanges, range)

    const data = {
      ...wizardData,
      ...(matchingRange && {
        id: matchingRange.id.toString(),
        attendanceEmployeesSettingId: matchingRange.attendanceEmployeesSettingId.toString(),
      }),
    }

    setNewSettingData(data)
  }

  const onUpdateSetting = () => {
    closeModalAndSubmit(wizardData)
  }

  const closeModalAndSubmit = async (data: WizardData) => {
    setOpenEffectiveAtFormModal(false)
    const success = await upsertRanges(data)

    if (success) onClose()
  }

  const onSubmitWizard = (data: WizardData) => {
    if (isEdit) {
      setWizardData(data)
      return setOpenEffectiveAtFormModal(true)
    }

    closeModalAndSubmit(data)
  }

  useEffect(() => {
    if (newSettingData) {
      closeModalAndSubmit(newSettingData)

      const routeParams = {
        id: 'time_tracking',
        settingId: newSettingData?.attendanceEmployeesSettingId.toString(),
        tabId: 'work_time_categories',
      }

      navigate(navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams))
    }
  }, [newSettingData?.id])

  let title = i18n.t('time_tracking.settings.time_blocks.categories.special_hours.wizard.title')
  let icon: LargeIcon = 'night'

  if (!!isEdit) {
    title = i18n.t('time_tracking.settings.time_blocks.categories.special_hours.wizard.edit_title')
    icon = 'document-edit'
  }

  const onCloseWrapper = () => {
    track(trackingEvents.timeTracking.specialHoursWizardClosedBeforeFinishing)

    onClose()
  }

  return (
    <>
      {!openEffectiveAtFormModal && (
        <Wizard.Modal
          size="regular"
          onClose={onCloseWrapper}
          title={title}
          icon={icon}
          steps={[basicDetailsStep(), hourlyRateStep(), compensationMethodStep()]}
          initialData={wizardData}
          onSubmit={onSubmitWizard}
          locked={isLocked}
        />
      )}
      {openEffectiveAtFormModal && (
        <ManageSettingVersionModal
          onCreate={onCreateSetting}
          onUpdate={onUpdateSetting}
          onClose={() => setOpenEffectiveAtFormModal(false)}
          running={false}
          attendanceSettingId={attendanceSetting.id}
          attendanceSettingEffectiveAt={attendanceSetting.attributes.effective_at}
        />
      )}
    </>
  )
}

export default SpecialHourWizard
